/* global firebase */
import {
  SET_APPS,
  SET_APPBUNDLES,
  SET_DEVICES,
  LOADING_UI,
  SET_INSTALLED_APPS,
  SET_CURRENT_USER,
  SET_ERRORS,
  CLEAR_ERRORS,
  SET_USERS,
  SET_DEVICE_GROUPS,
  SET_ORGANIZATIONS,
  SET_GLOBAL_APPS,
  SET_GLOBAL_APPBUNDLES,
  SET_API_KEYS,
  SET_DEVICE_SYSTEM_VERSIONS
} from "../types";
import { db } from "../../firebase/firebase";
import { SUPER_ADMIN, ADMIN } from "../../utils/userRoles";
import { map } from "../../utils/map";
import { serverTimestamp, getCurrentUserOrganizationRef } from "./utils";

let dbSubscriptions = [];

export const fetchUser = (uid, organizationId) => dispatch => {
  dispatch({ type: LOADING_UI, payload: true });
  db.collection("users")
    .where("uid", "==", uid)
    .where("organizationRef", "==", db.collection('organizations').doc(organizationId)) // need this for security rules to pass
    .limit(1)
    .get()
    .then(querySnapshot => {
      if (querySnapshot.size > 0) {
        dispatch({ type: CLEAR_ERRORS });
        let user = {};
        querySnapshot.forEach(doc => {
          user = doc.data();
          db.collection("users")
            .doc(doc.id)
            .update({ lastLogin: serverTimestamp() });
        });
        dispatch({ type: SET_CURRENT_USER, payload: user });
        fetchDB({ user, dispatch });
      } else {
        dispatch({ type: SET_ERRORS, payload: "Login Not Found" });
      }
    });
};

const fetchDB = ({ user: { role }, dispatch }) => {
  switch (role) {
    case SUPER_ADMIN:
      listenCollection("organizations", SET_ORGANIZATIONS, dispatch, "name");
      listenCollection("devices", SET_DEVICES, dispatch, "name");
      listenCollection("deviceGroups", SET_DEVICE_GROUPS, dispatch, "name");
      listenCollection("users", SET_USERS, dispatch, "name");
      listenCollection("apps", SET_APPS, dispatch, "title");
      listenCollection("contentBundles", SET_APPBUNDLES, dispatch, "name");
      listenCollection("deviceSystemVersions", SET_DEVICE_SYSTEM_VERSIONS, dispatch);
      listenCollection("apiKeys", SET_API_KEYS, dispatch);
      dispatch({ type: SET_GLOBAL_APPBUNDLES, payload: {} }); // hack to have this action get called for superAdmins
      dispatch({ type: SET_GLOBAL_APPS, payload: {} }); // hack to have this action get called for superAdmins
      break;
    case ADMIN:
      listenCollectionForOrganization("users", SET_USERS, dispatch, "name");
    default:
      listenUserOrganizationDocument(SET_ORGANIZATIONS, dispatch);
      listenCollectionForOrganization("devices", SET_DEVICES, dispatch, ["deviceGroupRef", "name"]);
      listenCollectionForOrganization(
        "deviceGroups",
        SET_DEVICE_GROUPS,
        dispatch,
        "name"
      );
      listenCollectionForOrganization("apps", SET_APPS, dispatch, "title");
      listenCollectionWithGlobalAccess("apps", SET_GLOBAL_APPS, dispatch, "title");
      listenCollectionForOrganization(
        "contentBundles",
        SET_APPBUNDLES,
        dispatch,
        "name"
      );
      listenCollectionWithGlobalAccess(
        "contentBundles",
        SET_GLOBAL_APPBUNDLES,
        dispatch,
        "name"
      );
      listenCollection("deviceSystemVersions", SET_DEVICE_SYSTEM_VERSIONS, dispatch);
      listenCollectionForOrganization("apiKeys", SET_API_KEYS, dispatch, "name");
      break;
  }
};

function createDocData(doc) {
  let docData = doc.data();
  docData._id = doc.id;
  if (docData.organizationRef) {
    docData._organizationId = docData.organizationRef.id;
  }

  return docData
}

export function fetchCollection(collection, actionType, dispatch, order) {
  dispatch({ type: LOADING_UI, payload: true });
  return db
    .collection(collection)
    .orderBy(order)
    .get()
    .then(querySnapshot => {
      let data = {};
      querySnapshot.forEach(doc => {
        data[doc.id] = createDocData(doc);
      });
      dispatch({ type: actionType, payload: data });
      dispatch({ type: LOADING_UI, payload: false });
    });
}

function listenCollection(collection, actionType, dispatch, order) {
  dispatch({ type: LOADING_UI, payload: true });
  let collectionQuery = db.collection(collection);
  if (order) {
    collectionQuery = collectionQuery.orderBy(order);
  }
  const listener = collectionQuery.onSnapshot(querySnapshot => {
    let data = {};
    querySnapshot.forEach(doc => {
      data[doc.id] = createDocData(doc);
    });
    dispatch({ type: actionType, payload: data });
    dispatch({ type: LOADING_UI, payload: false });
  });

  dbSubscriptions.push(listener);
}

function listenUserOrganizationDocument(actionType, dispatch) {
  dispatch({ type: LOADING_UI, payload: true });

  const listener = getCurrentUserOrganizationRef()
    .onSnapshot(doc => {
      let data = {};
      data[doc.id] = createDocData(doc);
      dispatch({ type: actionType, payload: data });
      dispatch({ type: LOADING_UI, payload: false });
    });

  dbSubscriptions.push(listener);
}

function listenCollectionForOrganization(collection, actionType, dispatch, order) {
  dispatch({ type: LOADING_UI, payload: true });

  if (typeof order === 'string') {
    order = [order];
  }

  let query = db.collection(collection);
  for (let i = 0; i < order.length; i++) {
    query = query.orderBy(order[i]);
  }

  let listener = query.where("organizationRef", "==", getCurrentUserOrganizationRef())
    .onSnapshot(querySnapshot => {
      let data = {};
      querySnapshot.forEach(doc => {
        data[doc.id] = createDocData(doc);
      });
      dispatch({ type: actionType, payload: data });
      dispatch({ type: LOADING_UI, payload: false });
    });

  dbSubscriptions.push(listener);
}

function listenCollectionWithGlobalAccess(collection, actionType, dispatch, order) {
  dispatch({ type: LOADING_UI, payload: true });

  const listener = db.collection(collection)
    .orderBy(order)
    .where("globalAccess", "==", true)
    .onSnapshot(querySnapshot => {
      let data = {};
      querySnapshot.forEach(doc => {
        data[doc.id] = createDocData(doc);
      });
      dispatch({ type: actionType, payload: data });
      dispatch({ type: LOADING_UI, payload: false });
    });

  dbSubscriptions.push(listener);
}

export const unsubscribe = () => {
  map(dbSubscriptions, s => s());
  dbSubscriptions = [];
}

export const fetchInstalledApps = deviceId => dispatch => {
  dispatch({ type: SET_INSTALLED_APPS, payload: {} });
  dispatch({ type: LOADING_UI, payload: true });
  return db
    .collection("devices/" + deviceId + "/installedApps")
    .orderBy("applicationName")
    .get()
    .then(querySnapshot => {
      let installedApps = {};
      querySnapshot.forEach(doc => {
        installedApps[doc.id] = createDocData(doc);
      });
      dispatch({ type: SET_INSTALLED_APPS, payload: { deviceId, installedApps } });
      dispatch({ type: LOADING_UI, payload: false });
    });
};
